/**
 * FuturesMarginReportPage — `/futures/margin-report`.
 *
 * 1:1 với Access `Report.rpFuturesMargin`:
 *   - Top header chia 2 zone: Hedging Config (trái) + BẢNG QUY ĐỔI (phải).
 *   - Hàng cấu hình thứ 2: Ký quỹ/lot Robusta · Arabica · USD/VND rate · Ngày.
 *     Nhãn "Dự phòng giá biến động" (cột 11–15: Market + tiền, UPL, Margin Excess, %, No.lots) + "Tỷ lệ ký quỹ cắt lỗ" (16–17)
 *     hàng đầu khối bank — khớp cột Market +$, UPL, Margin Excess, %, No.lots và 2 cột tỷ lệ (`report_rpFuturesMargin.txt`).
 *   - 3 bank (VietinBank → BIDV → MBBank) trong **một** `<Table>` (3 `<TableBody>`) để cột thẳng hàng một khối.
 *     Mỗi bank: banner + header + month rows + Total; rowSpan cột 7–17 như Access.
 *   - Màu chữ / nền động: `report_rpFuturesMargin.txt` `Private Sub color_page()` + `ForeColor` design-time
 *     (OLE 13369344→#CC6600, 2366701→#24190D cho Avg Price); heat OTE/UPL2 = RGB(248,255*r,255*r).
 *   - Grand Total row cuối cộng 3 bank.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { WIDE_SCROLL_WRAPPER } from '../../theme/responsive'
import { useReportZoom } from '../dashboard/_shared/useReportZoom'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useGetBankStatementLatestQuery,
  useGetFuturesReportQuery,
  useGetMarginReportBankDetailQuery,
  useGetMarginReportChangeValuesQuery,
  usePutMarginReportChangeValuesMutation,
} from '../../store/api/futuresControlApi'
import NumericInput from './NumericInput'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresFormDialogTitleTextSx,
} from './futuresAccessUi'

type ReportRow = Record<string, unknown>

const BANKS = [
  { key: 'Vietinbank', label: 'VietinBank' },
  { key: 'BIDV', label: 'BIDV' },
  { key: 'MBbank', label: 'MBBank' },
] as const

// Số dòng month/bank được render = số row backend trả về (không cap, không pad).
// Khi backend trả 0 dòng → render 1 placeholder để bank section vẫn hiển thị.

// ─── Master 17-col grid: align với Access `rpFuturesMargin` 1:1 ────────────
// Cột 1–6: per-month data (Month/Positions/Avg Price/Initial Margin/OTE-UPL/OTE-UPL+X$)
// Cột 7–10: current-state aggregate (Balance/Margin Excess/% Margin Excess/No. lots + "Ngày")
// Cột 11: scenario marker "Market +X$" (txtLblVTB ở Access — vertical text, rowspan toàn body)
// Cột 12–15: scenario aggregate (UPL/Margin Excess/% Margin Excess/No. lots)
// Cột 16–17: stoploss (Tỷ lệ Ký quỹ / Tỷ lệ Ký quỹ +X$)
// Web: bảng `table-layout: auto` + lưới header theo min-content — đủ chỗ text; scroll ngang/dọc một lần ở `reportShellSx`.
/** 17 cột Access — độ rộng theo nội dung (không % + fixed layout → tránh cắt chữ màn hẹp). */
const MARGIN_REPORT_GRID_COLUMNS = 'repeat(17, minmax(min-content, max-content))'

/** Lưới 17 cột đồng bộ với bảng: tối thiểu full khung, nở theo nội dung. */
const marginReportGridSx = {
  display: 'grid',
  gridTemplateColumns: MARGIN_REPORT_GRID_COLUMNS,
  columnGap: 0,
  width: 'max-content',
  minWidth: '100%',
}

// ─── Shared sx ──────────────────────────────────────────────────────────────

const commandBarSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.75,
  flexWrap: 'wrap' as const,
  px: 1,
  py: 0.5,
  mb: 0.75,
  borderRadius: 2,
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderBottom: '2px solid rgba(244, 223, 156, 0.55)',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf7 100%)',
  boxShadow: '0 1px 2px rgba(11, 54, 51, 0.04)',
}

const commandBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.15,
  minHeight: 22,
  fontSize: '0.66rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  color: 'primary.main',
  borderColor: 'primary.main',
}

const reportShellSx = {
  flex: 1,
  minHeight: 0,
  /** Một lớp scroll duy nhất cho toàn báo cáo (ngang + dọc). */
  overflowX: 'hidden',
  overflowY: 'auto',
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderRadius: '14px',
  bgcolor: 'var(--app-card-bg, #ffffff)',
  p: 1,
  boxShadow: 'var(--app-shadow-soft, 0 10px 30px -26px rgba(11, 54, 51, 0.42))',
}

const reportPageSx = {
  width: '100%',
  minWidth: { xs: 1180, md: 'auto' },
  minHeight: 0,
  overflow: 'visible',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffdf8 100%)',
  // Bỏ viền + radius lớp trong (giữ lớp ngoài reportShellSx) → chỉ còn 1 lớp border.
  border: 'none',
  p: 1.25,
  fontSize: '0.68rem',
  color: '#000000',
}

/** Tier 1 — tiêu đề sub-report: ĐỒNG BỘ report skin (Summary/Factory/PnL):
 *  nền cream #fdf9ed phẳng, chữ forest #0b3633, viền mảnh #dfe3e8, weight 800. */
const sectionTitleSx = {
  height: 20,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.66rem',
  fontWeight: 800,
  letterSpacing: '0.04em',
  color: '#0b3633',
  backgroundColor: '#fdf9ed',
  border: '1px solid #dfe3e8',
  // Đường dưới band tiêu đề = forest đặc #0b3633 KHỚP đường dưới hàng tên cột.
  borderBottom: '1px solid #0b3633',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  textTransform: 'uppercase' as const,
}

/** Bảng quy đổi / Dự phòng — tier 1b: cùng cream + viền trái forest mảnh. */
const propTitleSx = {
  ...sectionTitleSx,
  borderLeft: '1px solid #0b3633',
}

/** Tiêu đề khối Hedging / Ngày / Bảng quy đổi — chữ nhỏ hơn band section trong bảng. */
const topBannerSectionTitleSx = {
  ...sectionTitleSx,
  fontSize: '0.58rem',
  height: 18,
  letterSpacing: '0.05em',
}

const topBannerPropTitleSx = {
  ...propTitleSx,
  fontSize: '0.58rem',
  height: 18,
  letterSpacing: '0.05em',
}

/** Tier 2 — Tỷ lệ ký quỹ cắt lỗ (cùng họ TOTAL POSITIONS Summary): cream + forest, to/đậm hơn 1 cấp. */
const darkTitleSx = {
  ...sectionTitleSx,
  height: 22,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  borderBottom: '1px solid #0b3633',
}

const marginTotalRowSx = {
  fontWeight: 700,
  color: '#1a1a1a',
  backgroundColor: '#e8f1ee',
  borderTop: '1px solid #dfe3e8',
}

const marginGrandTotalRowSx = {
  fontWeight: 800,
  fontSize: '0.58rem',
  color: '#1a1a1a',
  backgroundColor: '#dceae5',
  borderTop: '3px double rgba(11, 54, 51, 0.4)',
}

const tableSx = {
  tableLayout: 'auto' as const,
  width: 'max-content',
  minWidth: '100%',
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
  mt: 0,
  '& th, & td': {
    height: 20,
    px: 0.35,
    py: 0.1,
    border: '1px solid #e8ecef',
    fontSize: '0.68rem',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    color: '#111111',
    textAlign: 'center',
  },
  '& td': {
    // Body đậm hơn 1 chút (đồng bộ Summary) — weight 600.
    fontWeight: 600,
  },
  '& th': {
    fontSize: '0.66rem',
    fontWeight: 800,
    letterSpacing: 'normal',
    color: '#0b3633',
    backgroundColor: '#fdf9ed',
    backgroundImage: 'none',
    borderBottom: '1px solid #0b3633',
    minWidth: 'min-content',
    px: 0.4,
  },
  '& tbody tr td': {
    bgcolor: '#ffffff',
  },
  '& td.num': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    minWidth: 'min-content',
    px: 0.4,
  },
  // 3 banner đồng bộ tone Summary: nền cream #fdf9ed + chữ forest + line đáy forest
  // (bỏ nền xanh/forest đặc → bớt "xanh quá"). Phân tầng bằng cỡ chữ + line trái.
  '& .bank-banner': {
    fontSize: '0.78rem',
    fontWeight: 800,
    color: '#0b3633',
    background: '#fdf9ed',
    borderBottom: '1px solid #0b3633',
    height: 24,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  '& .scenario-banner': {
    fontSize: '0.74rem',
    fontWeight: 800,
    color: '#0b3633',
    background: '#fdf9ed',
    borderBottom: '1px solid #0b3633',
    height: 24,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  '& .stoploss-banner': {
    fontSize: '0.74rem',
    fontWeight: 800,
    color: '#0b3633',
    background: '#fdf9ed',
    borderBottom: '1px solid #0b3633',
    borderLeft: '1px solid #0b3633',
    height: 24,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  '& td.span-cell': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: '#ffffff',
  },
  '& td.span-cell-scenario': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: '#ffffff',
  },
  '& td.span-cell-stoploss': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: '#ffffff',
  },
  '& tbody tr.total td': {
    ...marginTotalRowSx,
  },
  '& tbody tr.grand-total td': {
    ...marginGrandTotalRowSx,
  },
  /** Cột 1 Month: full_symbol dài (hàng header từng bank nằm trong tbody + `th`). */
  '& tbody tr.bank-subhead .MuiTableCell-root:first-of-type': {
    minWidth: 'max(5.5rem, min-content)',
  },
  '& tbody tr.month-row > .MuiTableCell-root:first-of-type': {
    minWidth: 'max(5.5rem, min-content)',
  },
  /** Hàng nhãn khối scenario/stoploss (chỉ VietinBank), ngay trên banner bank. */
  '& tbody tr.margin-bank-section-labels .MuiTableCell-root': {
    borderBottom: '1px solid rgba(11, 54, 51, 0.1)',
  },
  /** Khoảng cách giữa các bank trong cùng bảng. */
  '& tbody.bank-section-spacer .MuiTableCell-root': {
    border: 0,
    py: 0.5,
    height: 8,
    bgcolor: 'transparent',
    lineHeight: 0,
  },
}

/** Chiều cao hàng mặc định — khớp `tableSx` `& th, & td` height. */
const MARGIN_TABLE_ROW_HEIGHT_BASE = 20

/** Ước lượng chiều cao tối thiểu cho nhãn Market dọc (`writingMode: vertical-rl`). */
function estimateMarketVerticalLabelMinHeight(label: string): number {
  const fontPx = 0.56 * 16
  const charW = fontPx * 0.65
  return Math.ceil(label.length * charW + 6)
}

/** Chiều cao hàng dữ liệu (month); Total luôn `MARGIN_TABLE_ROW_HEIGHT_BASE`. */
function marginMonthRowHeight(monthRowCount: number, marketLabel: string): number {
  const totalH = MARGIN_TABLE_ROW_HEIGHT_BASE
  const labelMin = estimateMarketVerticalLabelMinHeight(marketLabel)
  const spanNatural = monthRowCount * totalH + totalH
  if (spanNatural >= labelMin || monthRowCount <= 0) return totalH
  return Math.ceil((labelMin - totalH) / monthRowCount)
}

const marginMonthRowSx = (height: number) => ({
  '& td': { height },
})

// ─── Formatters ─────────────────────────────────────────────────────────────

const displayMoney = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(n))
  if (n === 0) return '$ -'
  return n < 0 ? `$ (${formatted})` : `$ ${formatted}`
}

const displayMoney2 = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Math.abs(n),
  )
  if (n === 0) return '$ -'
  return n < 0 ? `$ (${formatted})` : `$ ${formatted}`
}

const displayVnd = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.abs(n))
  if (n === 0) return '0 ₫'
  return n < 0 ? `(${formatted}) ₫` : `${formatted} ₫`
}

const displayPercent = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const fmt = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 })
  if (n < 0) return `(${fmt.format(Math.abs(n))})`
  return fmt.format(n)
}

const displayLots = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(n))
  if (n === 0) return '0 lots'
  return n < 0 ? `(${formatted}) lots` : `${formatted} lots`
}

/** Lots display that preserves string sentinels (e.g. "Margin deficit" — view emits text
 * marker khi margin_excess âm). Number → "N lots"; string → as-is. */
const displayLotsOrText = (value: number | string | null): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string') return value
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(value))
  if (value === 0) return '0 lots'
  return value < 0 ? `(${formatted}) lots` : `${formatted} lots`
}

const displayPosition = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return '-'
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(n))
  return n < 0 ? `(${formatted})` : formatted
}

const displayNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Math.abs(n))
  if (n === 0) return '0'
  return n < 0 ? `(${formatted})` : formatted
}

const toRows = (items: unknown[] | undefined): ReportRow[] => (items ?? []) as ReportRow[]

const bankEquals = (row: ReportRow, bank: string) =>
  String(row.bank ?? '').trim().toLowerCase() === bank.toLowerCase()

const pickNumber = (row: ReportRow | undefined, ...keys: string[]): number | null => {
  if (!row) return null
  for (const key of keys) {
    const value = row[key]
    if (value === null || value === undefined || value === '') continue
    const n = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(n)) return n
  }
  return null
}

/** Returns numeric value when parseable; otherwise returns the raw non-empty string
 * (e.g. sentinel "Margin deficit" trong `no_lots_1`/`no_lots_2`). */
const pickNumberOrText = (row: ReportRow | undefined, ...keys: string[]): number | string | null => {
  if (!row) return null
  for (const key of keys) {
    const value = row[key]
    if (value === null || value === undefined || value === '') continue
    const n = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(n)) return n
    if (typeof value === 'string') {
      const s = value.trim()
      if (s) return s
    }
  }
  return null
}

const parseConfigNumber = (v: string | undefined): number => {
  if (!v) return 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const sumOrNull = (values: Array<number | null>): number | null => {
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v))
  if (finite.length === 0) return null
  return finite.reduce((a, b) => a + b, 0)
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

const todayStr = () => {
  const d = new Date()
  const dd = String(d.getDate()).padStart(2, '0')
  const mmm = MONTH_SHORT[d.getMonth()]
  return `${dd}-${mmm}-${d.getFullYear()}`
}

// ─── Conditional formatting (mirror Access VBA `color_page`) ────────────────
// Design-time `ForeColor` (OLE long → #RRGGBB): `frontend/plan/access_all/report_rpFuturesMargin.txt`
// (vd. txtVTBBalance / txtVTB61 `ForeColor =13369344`, txtVTB33 avg_price `ForeColor =2366701`).
// Runtime `color_page`: heat BackColor RGB(248,255*r,255*r); traffic % / tỷ lệ BackColor+ForeColor như VBA.

/** Access OLE `ForeColor = 13369344` → #CC6600 — hầu hết số tiền, lots, OTE/UPL, positions… (override #000000 per user 2026-05-21). */
const MARGIN_DESIGN_FORE_ORANGE = '#000000'
/** Access OLE `ForeColor = 2366701` → #24190D — cột Avg Price (per-month). (override #000000 per user 2026-05-21) */
const MARGIN_DESIGN_FORE_AVG_PRICE = '#000000'

const marginOrangeNumSx = { color: MARGIN_DESIGN_FORE_ORANGE, fontWeight: 700 as const }
const marginAvgPriceNumSx = { color: MARGIN_DESIGN_FORE_AVG_PRICE, fontWeight: 700 as const }

// Bộ màu traffic-light tone WEB TLC — TINT NHẠT + chữ đậm cùng hệ (giống các
// report khác: total #e8f1ee…), không fill đặc đậm. Giữ ngữ nghĩa tốt/cảnh báo/xấu.
const TL_GREEN = { bg: '#dcefe6', fg: '#1f6f4f' } // tốt — teal nhạt
const TL_AMBER = { bg: '#f7ecc9', fg: '#806015' } // cảnh báo — gold nhạt
const TL_RED = { bg: '#f4ddd4', fg: '#9c3f29' } // xấu — copper nhạt

/** Heat map cho OTE/UPL Market±X$ — tone nhạt: thấp (rủi ro) = copper nhạt
 *  #f0cdbf → cao = trắng #ffffff (nội suy theo ratio per-bank min/max). */
const heatMapBgColor = (value: number | null, min: number, max: number): string | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined
  const range = max - min
  if (range <= 0) return undefined
  const ratio = Math.max(0, Math.min(1, (value - min) / range))
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * ratio)
  // #f0cdbf (240,205,191) → #ffffff (255,255,255)
  const r = lerp(0xf0, 0xff)
  const g = lerp(0xcd, 0xff)
  const b = lerp(0xbf, 0xff)
  return `rgb(${r}, ${g}, ${b})`
}

/** 3-tier traffic light cho % Margin Excess/NLV (cột J & P).
 * - <0.1: đỏ | 0.1–0.4: vàng | >0.4: xanh lá. */
const trafficLight = (value: number | null): { bg: string; fg: string } | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined
  if (value < 0.1) return TL_RED
  if (value <= 0.4) return TL_AMBER
  return TL_GREEN
}

/** 3-tier traffic light RIÊNG cho Tỷ lệ Ký quỹ (cột S & T) — ngưỡng theo Excel
 *  (CF cellIs S/T): <1 đỏ | 1–1.4 vàng | >1.4 xanh lá. (KHÁC cột % ở mục trên.) */
const trafficLightRatio = (value: number | null): { bg: string; fg: string } | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined
  if (value < 1) return TL_RED
  if (value <= 1.4) return TL_AMBER
  return TL_GREEN
}

/** Banner scenario zone (txtLblVTB/BIDV/MB): tăng giá → forest-green, giảm → đỏ.
 *  Tone nhẹ (nền cream + CHỮ màu) thay vì fill xanh/đỏ đặc → đồng bộ Summary,
 *  vẫn giữ tín hiệu lên/xuống qua màu chữ. */
const scenarioBannerColor = (priceChange: number): { bg: string; fg: string } =>
  priceChange >= 0
    ? { bg: '#fdf9ed', fg: '#1f7a4d' }
    : { bg: '#fdf9ed', fg: '#c0392b' }

// ─── Change Input Value dialog ───────────────────────────────────────────────

/** Một position-month trên report = key `${symbol}_${bank}` trong report_change_value. */
interface PositionMonth {
  key: string
  symbol: string
  bankLabel: string
}

interface ChangeInputDialogProps {
  open: boolean
  initialValues: Record<string, string>
  positions: PositionMonth[]
  saving: boolean
  onClose: () => void
  onSave: (values: Record<string, string>) => void
}

const CHANGE_FIELDS: { name: string; label: string; integer?: boolean }[] = [
  { name: 'lot_hedge', label: 'Lots to hedge', integer: true },
  { name: 'price_change', label: 'Price change', integer: true },
  { name: 'change_vnd', label: 'Conversion (VND)' },
  { name: 'change_usd', label: 'Conversion (USD)' },
  { name: 'kyquy_robusta', label: 'Margin/Lot - Robusta' },
  { name: 'kyquy_arabica', label: 'Margin/Lot - Arabica' },
  { name: 'balance_vietinbank', label: 'Balance - VietinBank' },
  { name: 'balance_bidv', label: 'Balance - BIDV' },
  { name: 'balance_mbbank', label: 'Balance - MBBank' },
]

function ChangeInputValueDialog({ open, initialValues, positions, saving, onClose, onSave }: ChangeInputDialogProps) {
  const [state, setState] = useState<Record<string, string>>({})
  useEffect(() => {
    if (open) setState({ ...initialValues })
  }, [open, initialValues])

  const update = (name: string, value: string) => setState((prev) => ({ ...prev, [name]: value }))

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Futures Control - Margin Report — Change Input Value"
          sx={{ ...futuresFormDialogTitleTextSx, fontSize: '0.85rem' }}
        >
          Futures Control - Margin Report — Change Input Value
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => onSave(state)}
            disabled={saving}
            sx={futuresFormDialogActionBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={futuresFormDialogActionBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers>
        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              columnGap: 1.25,
              rowGap: 1.25,
            }}
          >
            {CHANGE_FIELDS.map((field) => (
              <NumericInput
                key={field.name}
                label={field.label}
                size="small"
                fullWidth
                allowNegative
                integer={field.integer}
                value={state[field.name] ?? ''}
                onChange={(raw) => update(field.name, raw)}
              />
            ))}
          </Box>

          {positions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Position month</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bank</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 140 }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((p) => (
                    <TableRow key={p.key}>
                      <TableCell>{p.symbol}</TableCell>
                      <TableCell>{p.bankLabel}</TableCell>
                      <TableCell>
                        <NumericInput
                          size="small"
                          fullWidth
                          allowNegative
                          value={state[p.key] ?? ''}
                          onChange={(raw) => update(p.key, raw)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

// ─── Per-bank totals (computed client-side, mirror Access DSum) ─────────────

interface BankTotals {
  positions: number | null
  initialMargin: number | null
  oteUpl: number | null
  oteUpl2: number | null
}

interface BankAggregate {
  balance: number | null
  marginExcess1: number | null
  perMarginExcess1: number | null
  noLots1: number | string | null
  upl: number | null
  marginExcess2: number | null
  perMarginExcess2: number | null
  noLots2: number | string | null
  tyLeKyQuy: number | null
  tyLeKyQuy2: number | null
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function FuturesMarginReportPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const margin = useGetFuturesReportQuery({ kind: 'margin-report', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const bankDetail = useGetMarginReportBankDetailQuery({ page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const changeValuesQ = useGetMarginReportChangeValuesQuery()
  // Tỷ giá USD/VND (Excel F9 = INDEX/MATCH MAX(Ngày Thông Báo)) — dùng endpoint
  // `bank-statement/latest` (dòng mới nhất theo ngày), KHÔNG lấy list page1/size1
  // (không đảm bảo mới nhất → tỷ giá rỗng → Conversion Table cột 2 ra "—").
  const vtbStatement = useGetBankStatementLatestQuery({ bank: 'Vietinbank' })
  const [putChangeValues, putState] = usePutMarginReportChangeValuesMutation()

  const [changeOpen, setChangeOpen] = useState(false)
  // Zoom report — cùng control + hành vi với Summary / dashboard PnL.
  const { zoom, control } = useReportZoom()

  const loading =
    margin.isLoading || bankDetail.isLoading || changeValuesQ.isLoading || vtbStatement.isLoading
  const fetching =
    margin.isFetching || bankDetail.isFetching || changeValuesQ.isFetching || vtbStatement.isFetching
  const hasError = Boolean(margin.error || bankDetail.error || changeValuesQ.error)

  const summaryRows = toRows(margin.data?.items)
  const bankDetailRows = toRows(bankDetail.data?.items)
  const cfg = changeValuesQ.data ?? {}

  const lotHedge = parseConfigNumber(cfg['lot_hedge'])
  const priceChange = parseConfigNumber(cfg['price_change'])
  const kyquyRobusta = parseConfigNumber(cfg['kyquy_robusta'])
  const kyquyArabica = parseConfigNumber(cfg['kyquy_arabica'])
  const changeUsd = parseConfigNumber(cfg['change_usd'])
  const changeVnd = parseConfigNumber(cfg['change_vnd'])

  const usdVndRate = useMemo(() => {
    const item = vtbStatement.data?.item ?? undefined
    return pickNumber(item, 'rateBuyCurrency', 'rate_buy_currency') ?? 0
  }, [vtbStatement.data?.item])

  const handleSave = async (values: Record<string, string>) => {
    try {
      await putChangeValues(values).unwrap()
      // Reload báo cáo + bank detail + change-values (PUT đã invalidate FuturesControl nhưng refetch tường minh để UI chắc chắn có số mới ngay).
      await Promise.all([
        margin.refetch(),
        bankDetail.refetch(),
        changeValuesQ.refetch(),
      ]).catch(() => undefined)
      snack.success('Margin Report configuration saved.')
      setChangeOpen(false)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  // ── Helpers per bank ────────────────────────────────────────────────────
  const bankRows = (bank: string): ReportRow[] =>
    bankDetailRows
      .filter((r) => bankEquals(r, bank))
      .sort((a, b) => (pickNumber(a, 'rank') ?? 0) - (pickNumber(b, 'rank') ?? 0))

  // Tất cả position-month trên report (mỗi bank theo thứ tự hiển thị) → input giá 1 lượt.
  const positionRows: PositionMonth[] = BANKS.flatMap((b) =>
    bankRows(b.key)
      .map((r) => {
        const symbol = String(r.fullSymbol ?? r.month ?? '').trim()
        return { key: `${symbol}_${b.key}`, symbol, bankLabel: b.label }
      })
      .filter((p) => p.symbol),
  )

  const bankAgg = (bank: string): BankAggregate => {
    const row = summaryRows.find((r) => bankEquals(r, bank))
    return {
      balance: pickNumber(row, 'balance'),
      marginExcess1: pickNumber(row, 'marginExcess1', 'margin_excess_1'),
      perMarginExcess1: pickNumber(row, 'perMarginExcess1', 'per_margin_excess_1'),
      noLots1: pickNumberOrText(row, 'noLots1', 'no_lots_1'),
      upl: pickNumber(row, 'upl'),
      marginExcess2: pickNumber(row, 'marginExcess2', 'margin_excess_2'),
      perMarginExcess2: pickNumber(row, 'perMarginExcess2', 'per_margin_excess_2'),
      noLots2: pickNumberOrText(row, 'noLots2', 'no_lots_2'),
      tyLeKyQuy: pickNumber(row, 'tyLeKyQuy', 'ty_le_ky_quy'),
      tyLeKyQuy2: pickNumber(row, 'tyLeKyQuy2', 'ty_le_ky_quy_2'),
    }
  }

  const bankTotals = (rows: ReportRow[]): BankTotals => ({
    positions: sumOrNull(rows.map((r) => Math.abs(pickNumber(r, 'openPosition') ?? 0) || null)),
    initialMargin: sumOrNull(rows.map((r) => Math.abs(pickNumber(r, 'initialMargin') ?? 0) || null)),
    oteUpl: sumOrNull(rows.map((r) => pickNumber(r, 'oteUpl'))),
    oteUpl2: sumOrNull(rows.map((r) => pickNumber(r, 'oteUpl2'))),
  })

  // ── Header config: BẢNG QUY ĐỔI numbers ─────────────────────────────────
  // VND→USD: số VND nhập (change_vnd ≙ Excel M4) / tỷ giá.
  const vndToUsd = changeVnd && usdVndRate ? changeVnd / usdVndRate : 0
  const lotsNeed = lotHedge && kyquyRobusta ? lotHedge * kyquyRobusta : 0 // Ký quỹ khả dụng cần (USD)
  const lotsNeedVnd = lotsNeed && usdVndRate ? lotsNeed * usdVndRate : 0

  // Dynamic labels — mirror Access ControlSource trên `Label558` và `Label2303`:
  //   OTE/UPL: `IIf(price>0, "OTE/UPL (+X$)", "OTE/UPL (X$)")` (KHÔNG có "Market")
  //   Tỷ lệ Ký quỹ: `IIf(price>0, "Tỷ lệ Ký quỹ (+X$)", ...)` (giữ nguyên capital K)
  const priceTag = `${priceChange > 0 ? '+' : ''}${priceChange}$`
  const oteUpl2Label = priceChange ? `OTE/UPL (${priceTag})` : 'OTE/UPL'
  const tyLeKyQuy2Label = priceChange ? `Margin Ratio (${priceTag})` : 'Margin Ratio'
  // Vertical scenario banner — txtLblVTB ControlSource: `Market +X$` / `Market X$`.
  const marketLabel = `Market ${priceTag}`

  // ── Grand totals ─────────────────────────────────────────────────────────
  const allBankAggs = BANKS.map((b) => bankAgg(b.key))
  const allBankTotals = BANKS.map((b) => bankTotals(bankRows(b.key)))

  const grandTotalPositions = sumOrNull(allBankTotals.map((t) => t.positions))
  const grandTotalInitialMargin = sumOrNull(allBankTotals.map((t) => t.initialMargin))
  const grandTotalOteUpl = sumOrNull(allBankTotals.map((t) => t.oteUpl))
  const grandTotalOteUpl2 = sumOrNull(allBankTotals.map((t) => t.oteUpl2))
  const grandTotalBalance = sumOrNull(allBankAggs.map((a) => a.balance))
  // USD→VND: Excel M5 = =H38 = TỔNG balance (live) × tỷ giá — KHÔNG dùng config change_usd.
  const usdToVnd = grandTotalBalance && usdVndRate ? grandTotalBalance * usdVndRate : 0
  const grandTotalMarginExcess1 = sumOrNull(allBankAggs.map((a) => a.marginExcess1))
  const grandTotalUpl = sumOrNull(allBankAggs.map((a) => a.upl))
  const grandTotalMarginExcess2 = sumOrNull(allBankAggs.map((a) => a.marginExcess2))
  const grandTotalNoLots1 =
    grandTotalMarginExcess1 != null && grandTotalMarginExcess1 > 0 && kyquyRobusta
      ? Math.round(grandTotalMarginExcess1 / kyquyRobusta)
      : null
  const grandTotalNoLots2 =
    grandTotalMarginExcess2 != null && grandTotalMarginExcess2 > 0 && kyquyRobusta
      ? Math.round(grandTotalMarginExcess2 / kyquyRobusta)
      : null

  // Fragment-returning helper: 2 grid items (label + value) so parent grid can
  // align label-width across rows (auto-sized columns).
  const renderConfigField = (label: string, value: string, compact?: boolean, valueSx?: object) => {
    const labelFs = compact ? '0.58rem' : '0.66rem'
    const valueFs = compact ? '0.62rem' : '0.7rem'
    return (
      <>
        <Typography
          component="span"
          sx={{
            fontSize: labelFs,
            fontWeight: 700,
            color: '#000000',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid rgba(11, 54, 51, 0.20)',
            background: '#FFFFFF',
            fontSize: valueFs,
            fontWeight: 700,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum"',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...valueSx,
          }}
        >
          {value || '—'}
        </Box>
      </>
    )
  }

  const renderBankTableBody = (bank: (typeof BANKS)[number]) => {
    const rows = bankRows(bank.key)
    const agg = bankAgg(bank.key)
    const totals = bankTotals(rows)
    // Dynamic row count: dùng đúng số row trả về; nếu 0 thì 1 placeholder.
    const displayRows: (ReportRow | null)[] = rows.length === 0 ? [null] : rows
    // rowSpan cho các span cells = số month rows + 1 (Total row)
    const aggRowSpan = displayRows.length + 1
    const monthRowHeight = marginMonthRowHeight(displayRows.length, marketLabel)
    const monthRowSx = marginMonthRowSx(monthRowHeight)

    // Heat map range: per-bank min/max of ote_upl2 across actual rows.
    const ote2Values = rows
      .map((r) => pickNumber(r, 'oteUpl2'))
      .filter((v): v is number => v != null && Number.isFinite(v))
    const ote2Min = ote2Values.length ? Math.min(...ote2Values) : 0
    const ote2Max = ote2Values.length ? Math.max(...ote2Values) : 0

    // Traffic-light colors cho 4 span cells (per-bank aggregate).
    const tlME1 = trafficLight(agg.perMarginExcess1)
    const tlME2 = trafficLight(agg.perMarginExcess2)
    const tlTy1 = trafficLightRatio(agg.tyLeKyQuy)
    const tlTy2 = trafficLightRatio(agg.tyLeKyQuy2)

    // Scenario banner: green/red theo price_change (mirror txtLbl* VBA).
    const scenarioBnr = scenarioBannerColor(priceChange)
    // Marker dọc "Market +X$" (Excel M13:M20) có CF expression riêng: $F$5>0 → xanh
    // đặc #00B050, <0 → đỏ #FF0000, chữ trắng. KHÁC banner nhãn bank (giữ tone nhẹ).
    const marketMarker =
      priceChange > 0
        ? TL_GREEN
        : priceChange < 0
          ? TL_RED
          : { bg: scenarioBnr.bg, fg: scenarioBnr.fg }

    return (
      <TableBody>
        {bank.key === 'Vietinbank' ? (
          <TableRow className="margin-bank-section-labels">
            <TableCell colSpan={10} sx={{ bgcolor: '#fffefb', verticalAlign: 'middle' }} />
            <TableCell
              colSpan={5}
              sx={{
                '&&': {
                  ...propTitleSx,
                  display: 'table-cell',
                  height: 22,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  backgroundImage: 'none',
                  py: 0.35,
                },
              }}
            >
              Price Movement Scenario
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                '&&': {
                  ...darkTitleSx,
                  display: 'table-cell',
                  height: 22,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  backgroundImage: 'none',
                  py: 0.35,
                },
              }}
            >
              Stop-loss margin ratio
            </TableCell>
          </TableRow>
        ) : null}
        <TableRow>
          <TableCell component="th" className="bank-banner" colSpan={10} scope="colgroup">
            {bank.label}
          </TableCell>
          <TableCell
            component="th"
            className="scenario-banner"
            colSpan={5}
            scope="colgroup"
            sx={{ backgroundColor: scenarioBnr.bg, color: scenarioBnr.fg, backgroundImage: 'none' }}
          >
            {bank.label}
          </TableCell>
          <TableCell component="th" className="stoploss-banner" colSpan={2} scope="colgroup">
            {bank.label}
          </TableCell>
        </TableRow>
        <TableRow className="bank-subhead">
          <TableCell component="th" scope="col">
            Month
          </TableCell>
          <TableCell component="th" scope="col">
            Positions
          </TableCell>
          <TableCell component="th" scope="col">
            Avg Price
          </TableCell>
          <TableCell component="th" scope="col">
            Initial Margin
          </TableCell>
          <TableCell component="th" scope="col">
            OTE/UPL
          </TableCell>
          <TableCell component="th" scope="col">
            {oteUpl2Label}
          </TableCell>
          <TableCell component="th" scope="col">
            Balance
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Excess
          </TableCell>
          <TableCell component="th" scope="col">
            % Margin Excess/NLV
          </TableCell>
          <TableCell component="th" scope="col">
            No. lots
          </TableCell>
          {/* col 11 sub-header placeholder — Market scenario banner cell render trong
              body row 1 với rowSpan để text dọc spans toàn data + total. */}
          <TableCell
            component="th"
            scope="col"
            sx={{ '&&&': { backgroundColor: marketMarker.bg, color: marketMarker.fg, backgroundImage: 'none' } }}
          />
          <TableCell component="th" scope="col">
            UPL
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Excess
          </TableCell>
          <TableCell component="th" scope="col">
            % Margin Excess/NLV
          </TableCell>
          <TableCell component="th" scope="col">
            No. lots
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Ratio
          </TableCell>
          <TableCell component="th" scope="col">
            {tyLeKyQuy2Label}
          </TableCell>
        </TableRow>
        {displayRows.map((row, idx) => {
          const ote2 = row ? pickNumber(row, 'oteUpl2') : null
          const heatBg = heatMapBgColor(ote2, ote2Min, ote2Max)
          return (
            <TableRow key={`row-${bank.key}-${idx}`} className="month-row" sx={monthRowSx}>
              <TableCell>{row ? String(row.fullSymbol ?? row.month ?? '') : ''}</TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayPosition(pickNumber(row, 'openPosition')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginAvgPriceNumSx}>
                {row ? displayMoney(pickNumber(row, 'avgPrice')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayMoney(pickNumber(row, 'initialMargin')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayMoney(pickNumber(row, 'oteUpl')) : ''}
              </TableCell>
              <TableCell
                className="num"
                sx={
                  heatBg
                    ? { '&&&': { backgroundColor: heatBg, color: MARGIN_DESIGN_FORE_ORANGE, fontWeight: 700 } }
                    : marginOrangeNumSx
                }
              >
                {row ? displayMoney(ote2) : ''}
              </TableCell>
              {idx === 0 ? (
                <>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.balance)}
                  </TableCell>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.marginExcess1)}
                  </TableCell>
                  <TableCell
                    className="num span-cell"
                    rowSpan={aggRowSpan}
                    sx={
                      tlME1
                        ? { '&&&': { backgroundColor: tlME1.bg, color: tlME1.fg, fontWeight: 800 } }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.perMarginExcess1)}
                  </TableCell>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayLotsOrText(agg.noLots1)}
                  </TableCell>
                  {/* col 11 — Market scenario marker (vertical text, txtLblVTB Access).
                      Mã màu xanh/đỏ theo price_change scenario; rowSpan spans toàn body + total. */}
                  <TableCell
                    rowSpan={aggRowSpan}
                    sx={{
                      '&&&': { backgroundColor: marketMarker.bg, color: marketMarker.fg },
                      padding: '2px 0 !important',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      // Nhãn dọc "Market +X$" cần bề ngang đủ rộng (mặc định chỉ bằng
                      // chiều cao chữ xoay nên quá hẹp).
                      width: 26,
                      minWidth: 26,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontWeight: 800,
                        letterSpacing: '0.02em',
                        fontSize: '0.56rem',
                        whiteSpace: 'nowrap',
                        mx: 'auto',
                      }}
                    >
                      {marketLabel}
                    </Box>
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.upl)}
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.marginExcess2)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-scenario"
                    rowSpan={aggRowSpan}
                    sx={
                      tlME2
                        ? { '&&&': { backgroundColor: tlME2.bg, color: tlME2.fg, fontWeight: 800 } }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.perMarginExcess2)}
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayLotsOrText(agg.noLots2)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-stoploss"
                    rowSpan={aggRowSpan}
                    sx={
                      tlTy1
                        ? { '&&&': { backgroundColor: tlTy1.bg, color: tlTy1.fg, fontWeight: 800 } }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.tyLeKyQuy)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-stoploss"
                    rowSpan={aggRowSpan}
                    sx={
                      tlTy2
                        ? { '&&&': { backgroundColor: tlTy2.bg, color: tlTy2.fg, fontWeight: 800 } }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.tyLeKyQuy2)}
                  </TableCell>
                </>
              ) : null}
            </TableRow>
          )
        })}
        <TableRow className="total">
          <TableCell>Total</TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayPosition(totals.positions)}
          </TableCell>
          <TableCell />
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.initialMargin)}
          </TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.oteUpl)}
          </TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.oteUpl2)}
          </TableCell>
          {/* cols 7–16 are merged from the first month row via rowSpan */}
        </TableRow>
      </TableBody>
    )
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={commandBarSx}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 'auto', ml: 0.5 }}>
          {/* <Typography variant="caption" sx={{ fontWeight: 700, color: '#000000' }}>
            Margin Report
          </Typography> */}
          {fetching && !loading ? <CircularProgress size={12} sx={{ color: 'primary.main' }} /> : null}
        </Stack>
        {control}
        <Button
          size="small"
          variant="outlined"
          sx={commandBtnSx}
          disabled={!canEdit}
          onClick={() => setChangeOpen(true)}
        >
          Change Input Value
        </Button>
      </Box>

      <Box sx={reportShellSx}>
        {hasError ? (
          <Alert severity="error" sx={{ mb: 1, borderRadius: 2, fontSize: '0.72rem', py: 0.5 }}>
            Could not load part of the Margin Report data. Please try again later.
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <>
          {/* Reload (refetch) → strip + dim, giữ report cũ (mirror DataframeBody). */}
          {fetching ? <LinearProgress sx={{ height: 2, borderRadius: 1, mb: 0.5 }} /> : null}
          <Box sx={{ ...WIDE_SCROLL_WRAPPER, opacity: fetching ? 0.55 : 1, pointerEvents: fetching ? 'none' : 'auto', transition: 'opacity 0.15s' }}>
          <Box sx={{ ...reportPageSx, zoom }}>
            {/* ── Top header row 1: Hedging Config (cols 1-9) + Ngày (col 10) + BẢNG QUY ĐỔI (cols 11-17)
                Layout 17-col: cột 10 ("Ngày") nằm trên đúng cột "No. lots" của bank table (Access R7 C9). */}
            <Box sx={marginReportGridSx}>
              <Box sx={{ gridColumn: '1 / 10', display: 'flex', flexDirection: 'column', pr: 0.75 }}>
                <Box sx={topBannerSectionTitleSx}>Hedging Configuration</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'grid',
                    // 2 cặp label-value mỗi hàng → 4 cột (auto label / 1fr value) × 2
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto minmax(0, 1fr)',
                    columnGap: 1.5,
                    rowGap: 0.5,
                    bgcolor: '#fffefb',
                  }}
                >
                  {renderConfigField('Lots to hedge', lotHedge ? `${displayNumber(lotHedge)} lots` : '—', true)}
                  {renderConfigField(
                    `Total P&L when Market ${priceChange > 0 ? '+' : ''}${priceChange || 0}$`,
                    displayMoney2(grandTotalMarginExcess2),
                    true,
                  )}
                  {renderConfigField('Required margin excess (USD)', lotsNeed ? displayMoney2(lotsNeed) : '—', true)}
                  {renderConfigField('In VND', lotsNeedVnd ? displayVnd(lotsNeedVnd) : '—', true)}
                  {renderConfigField(
                    'Price change',
                    priceChange ? `${priceChange > 0 ? '+' : ''}${priceChange} $` : '—',
                    true,
                    // CF F5 (tone web): >0 → teal-green, <0 → copper, chữ trắng bold.
                    priceChange > 0
                      ? { background: TL_GREEN.bg, color: TL_GREEN.fg }
                      : priceChange < 0
                        ? { background: TL_RED.bg, color: TL_RED.fg }
                        : undefined,
                  )}
                  {/* Tỷ giá USD/VND (Excel F9) — cùng hàng Ký quỹ/lot như Excel. */}
                  {renderConfigField('USD/VND Rate', usdVndRate ? displayNumber(usdVndRate) : '—', true)}
                  {renderConfigField('Margin/Lot - Arabica', kyquyArabica ? displayMoney2(kyquyArabica) : '—', true)}
                  {renderConfigField('Margin/Lot - Robusta', kyquyRobusta ? displayMoney2(kyquyRobusta) : '—', true)}
                </Box>
              </Box>

              {/* col 10 — "Ngày" cell aligned ngay trên "No. lots" của bank table (Access R7 C9 + R8 C9=Date()). */}
              <Box sx={{ gridColumn: '10 / 11', display: 'flex', flexDirection: 'column', px: 0.25 }}>
                <Box sx={topBannerSectionTitleSx}>Date</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    bgcolor: '#fffefb',
                    flex: 1,
                  }}
                >
                  {todayStr()}
                </Box>
              </Box>

              <Box sx={{ gridColumn: '11 / 18', display: 'flex', flexDirection: 'column', pl: 0.5 }}>
                <Box sx={topBannerPropTitleSx}>Conversion Table</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'grid',
                    // 1 cặp label-value mỗi hàng → 2 cột (auto / 1fr)
                    gridTemplateColumns: 'auto minmax(0, 1fr)',
                    columnGap: 1,
                    rowGap: 0.5,
                    bgcolor: '#fffefb',
                  }}
                >
                  {renderConfigField(
                    `Convert to VND (${grandTotalBalance ? displayNumber(grandTotalBalance) : '—'} USD)`,
                    usdToVnd ? displayVnd(usdToVnd) : '—',
                    true,
                  )}
                  {renderConfigField(
                    `Convert to USD (${changeVnd ? displayNumber(changeVnd) : '—'} VND)`,
                    vndToUsd ? displayMoney2(vndToUsd) : '—',
                    true,
                  )}
                </Box>
              </Box>
            </Box>

            {/* ── 3 bank + Grand Total: một Table để cột thẳng hàng một khối ── */}
            <Box sx={{ mt: 1.25, width: 'max-content', minWidth: '100%' }}>
              <Table size="small" sx={tableSx}>
                {BANKS.map((bank, bankIdx) => (
                  <Fragment key={bank.key}>
                    {renderBankTableBody(bank)}
                    {bankIdx < BANKS.length - 1 ? (
                      <TableBody className="bank-section-spacer">
                        <TableRow>
                          <TableCell colSpan={17} />
                        </TableRow>
                      </TableBody>
                    ) : null}
                  </Fragment>
                ))}
                <TableBody>
                  <TableRow className="grand-total">
                    <TableCell>Grand Total</TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayPosition(grandTotalPositions)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalInitialMargin)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalOteUpl)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalOteUpl2)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalBalance)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalMarginExcess1)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {grandTotalNoLots1 != null ? displayLots(grandTotalNoLots1) : ''}
                    </TableCell>
                    {/* col 11: empty in grand total (Market marker chỉ áp dụng per-bank) */}
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalUpl)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalMarginExcess2)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {grandTotalNoLots2 != null ? displayLots(grandTotalNoLots2) : ''}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
          </Box>
          </>
        )}
      </Box>

      <ChangeInputValueDialog
        open={changeOpen}
        initialValues={cfg}
        positions={positionRows}
        saving={putState.isLoading}
        onClose={() => setChangeOpen(false)}
        onSave={(v) => void handleSave(v)}
      />
    </Box>
  )
}
